import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  getContactsList,
  getContactPreview,
  getContactPhones,
  getContactEmails,
  getContactAddresses,
  createContactPhone,
  updateContactPhone,
  deleteContactPhone,
  createContactEmail,
  updateContactEmail,
  deleteContactEmail,
} from "../../../../services/contacts";

const ContactsContext = createContext();

const isBodyEmpty = (body) => {
  if (body === null || body === undefined) return true;
  if (Array.isArray(body)) return body.length === 0;
  if (typeof body === 'object') return Object.keys(body).length === 0;
  return false;
};

const initialState = {
  contacts: [],
  contactsMeta: null,
  selectedContact: null,
  contactDetails: null,
  emails: [],
  phones: [],
  addresses: [],
  loading: false,
  loadingContacts: false,
  loadingDetails: false,
  loadingPhones: false,
  loadingEmails: false,
  error: null,
  isEmpty: false,
};

export const ContactsProvider = ({ children }) => {
  const [state, setState] = useState(initialState);

  const phonesFetchId = useRef(0);
  const emailsFetchId = useRef(0);

  const updateState = useCallback((updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const applyPrimaryEmailLocally = useCallback((emailId, emailAddress) => {
    setState((prev) => {
      const updatedEmails = (prev.emails || []).map((e) => ({
        ...e,
        isPrimary: e.partyEmailUuid === emailId,
      }));
      const updatedContact = prev.contactDetails
        ? { ...prev.contactDetails, email: emailAddress }
        : prev.contactDetails;
      return { ...prev, emails: updatedEmails, contactDetails: updatedContact };
    });
  }, []);

  const applyPrimaryPhoneLocally = useCallback((phoneId, phoneNumber) => {
    setState((prev) => {
      const updatedPhones = (prev.phones || []).map((p) => ({
        ...p,
        isPrimary: p.partyPhoneUuid === phoneId,
      }));
      const updatedContact = prev.contactDetails
        ? { ...prev.contactDetails, phone: phoneNumber }
        : prev.contactDetails;
      return { ...prev, phones: updatedPhones, contactDetails: updatedContact };
    });
  }, []);

  const fetchContacts = useCallback(
    async (params = {}) => {
      updateState({ loadingContacts: true, error: null, isEmpty: false });
      try {
        const response = await getContactsList({
          page: params.page || 1,
          page_size: params.page_size || 20,
          order_by: params.order_by || ["createdAt"],
          order_direction: params.order_direction || ["DESC"],
          include_deleted: params.include_deleted || false,
          search: params.search || null,
          filters: params.filters || null,
        });
        
        if (response?.statusCode !== 200) {
          throw new Error(`API returned status code ${response?.statusCode}`);
        }
        
        const body = response.body;
        const isEmpty = isBodyEmpty(body);
        
        updateState({
          contacts: Array.isArray(body) ? body : [],
          contactsMeta: response.meta || null,
          loadingContacts: false,
          error: null,
          isEmpty,
        });
      } catch (err) {
        console.error("Error fetching contacts:", err);
        updateState({ loadingContacts: false, error: err, isEmpty: false });
      }
    },
    [updateState]
  );

  const fetchContactById = useCallback(
    async (contactId) => {
      updateState({ loadingDetails: true, error: null });
      try {
        const response = await getContactPreview(contactId);
        updateState({
          contactDetails: response.body || null,
          selectedContact: response.body || null,
          loadingDetails: false,
          error: null,
        });
      } catch (err) {
        console.error("Error fetching contact by ID:", err);
        updateState({ loadingDetails: false, error: err });
      }
    },
    [updateState]
  );

  const fetchContactEmails = useCallback(
    async (contactId, params = {}) => {
      try {
        updateState({ loadingEmails: true });
        const id = ++emailsFetchId.current;
        const response = await getContactEmails({
          party_uuid: contactId,
          ...params,
        });
        if (id === emailsFetchId.current) {
          updateState({
            emails: Array.isArray(response.body) ? response.body : [],
            loadingEmails: false,
          });
        }
      } catch (err) {
        console.error("Error fetching contact emails:", err);
        updateState({ loadingEmails: false });
      }
    },
    [updateState]
  );

  const fetchContactPhones = useCallback(
    async (contactId, params = {}) => {
      try {
        updateState({ loadingPhones: true });
        const id = ++phonesFetchId.current;
        const response = await getContactPhones({
          party_uuid: contactId,
          ...params,
        });
        if (id === phonesFetchId.current) {
          updateState({
            phones: Array.isArray(response.body) ? response.body : [],
            loadingPhones: false,
          });
        }
      } catch (err) {
        console.error("Error fetching contact phones:", err);
        updateState({ loadingPhones: false });
      }
    },
    [updateState]
  );

  const fetchContactAddresses = useCallback(
    async (contactId, params = {}) => {
      try {
        const response = await getContactAddresses({
          party_uuid: contactId,
          ...params,
        });
        updateState({
          addresses: Array.isArray(response.body) ? response.body : [],
        });
      } catch (err) {
        console.error("Error fetching contact addresses:", err);
      }
    },
    [updateState]
  );

  const createPhone = useCallback(
    async (contactId, phoneData) => {
      updateState({ error: null });
      try {
        const response = await createContactPhone({
          party_uuid: contactId,
          phone_type: phoneData.phone_type,
          phone_number: phoneData.phone_number,
          is_primary: phoneData.is_primary,
        });
        if (response.status_code === 201) await fetchContactPhones(contactId);
        return response;
      } catch (err) {
        console.error("[ContactsContext] Error creating contact phone:", err);
        updateState({ error: err });
        throw err;
      }
    },
    [updateState, fetchContactPhones]
  );

  const updatePhone = useCallback(
    async (contactId, phoneId, phoneData) => {
      updateState({ error: null });
      try {
        const response = await updateContactPhone({
          party_uuid: contactId,
          party_phone_uuid: phoneId,
          phone_number: phoneData.phone_number,
          is_primary: phoneData.is_primary,
        });
        if (response.status_code === 200) await fetchContactPhones(contactId);
        return response;
      } catch (err) {
        console.error("Error updating contact phone:", err);
        updateState({ error: err });
        throw err;
      }
    },
    [updateState, fetchContactPhones]
  );

  const deletePhone = useCallback(
    async (contactId, phoneId) => {
      updateState({ error: null });
      try {
        const response = await deleteContactPhone({
          party_uuid: contactId,
          party_phone_uuid: phoneId,
        });

        if (response.status_code === 200) {
          await fetchContactPhones(contactId);
        }

        return response;
      } catch (err) {
        console.error("Error deleting contact phone:", err);
        updateState({ error: err });
        throw err;
      }
    },
    [updateState, fetchContactPhones]
  );

  const createEmail = useCallback(
    async (contactId, emailData) => {
      updateState({ error: null });
      try {
        const response = await createContactEmail({
          party_uuid: contactId,
          email_type: emailData.email_type,
          email_address: emailData.email_address,
          is_primary: emailData.is_primary,
        });
        if (response.status_code === 201) await fetchContactEmails(contactId);
        return response;
      } catch (err) {
        console.error("[ContactsContext] Error creating contact email:", err);
        updateState({ error: err });
        throw err;
      }
    },
    [updateState, fetchContactEmails]
  );

  const updateEmail = useCallback(
    async (contactId, emailId, emailData) => {
      updateState({ error: null });
      try {
        const response = await updateContactEmail({
          party_uuid: contactId,
          party_email_uuid: emailId,
          email_address: emailData.email_address,
          is_primary: emailData.is_primary,
        });
        if (response.status_code === 200) await fetchContactEmails(contactId);
        return response;
      } catch (err) {
        console.error("Error updating contact email:", err);
        updateState({ error: err });
        throw err;
      }
    },
    [updateState, fetchContactEmails]
  );

  const deleteEmail = useCallback(
    async (contactId, emailId) => {
      updateState({ error: null });
      try {
        const response = await deleteContactEmail({
          party_uuid: contactId,
          party_email_uuid: emailId,
        });

        if (response.status_code === 200) {
          await fetchContactEmails(contactId);
        }

        return response;
      } catch (err) {
        console.error("Error deleting contact email:", err);
        updateState({ error: err });
        throw err;
      }
    },
    [updateState, fetchContactEmails]
  );

  const setSelectedContact = useCallback(
    (contact) => {
      updateState({ selectedContact: contact });
    },
    [updateState]
  );

  // Track in-flight requests to prevent duplicate simultaneous calls
  const inFlightRequestsRef = useRef(new Set());

  const fetchContactDetails = useCallback(
    async (contactId) => {
      // Check if we're already loading this contact (prevent simultaneous calls)
      if (inFlightRequestsRef.current.has(contactId)) {
        return;
      }

      // Mark as in-flight
      inFlightRequestsRef.current.add(contactId);

      try {
        await Promise.all([
          fetchContactById(contactId),
          fetchContactEmails(contactId),
          fetchContactPhones(contactId),
          fetchContactAddresses(contactId),
        ]);
      } finally {
        // Always remove from set when done (success or error) to allow future refreshes
        inFlightRequestsRef.current.delete(contactId);
      }
    },
    [
      fetchContactById,
      fetchContactEmails,
      fetchContactPhones,
      fetchContactAddresses,
    ]
  );

  const clearContacts = useCallback(() => {
    setState(initialState);
    // Clear in-flight requests tracking
    inFlightRequestsRef.current.clear();
  }, []);

  const clearContactDetails = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedContact: null,
      contactDetails: null,
      emails: [],
      phones: [],
      addresses: [],
      loadingDetails: false,
      loadingPhones: false,
      loadingEmails: false,
    }));
    // Clear in-flight requests tracking
    inFlightRequestsRef.current.clear();
  }, []);

  const value = {
    contacts: state.contacts,
    contactsMeta: state.contactsMeta,
    selectedContact: state.selectedContact,
    contactDetails: state.contactDetails,
    emails: state.emails,
    phones: state.phones,
    addresses: state.addresses,
    loading: state.loading,
    loadingContacts: state.loadingContacts,
    loadingDetails: state.loadingDetails,
    loadingPhones: state.loadingPhones,
    loadingEmails: state.loadingEmails,
    error: state.error,
    isEmpty: state.isEmpty,

    fetchContacts,
    fetchContactById,
    fetchContactEmails,
    fetchContactPhones,
    fetchContactAddresses,
    fetchContactDetails,
    setSelectedContact,
    clearContacts,
    clearContactDetails,

    createPhone,
    updatePhone,
    deletePhone,
    createEmail,
    updateEmail,
    deleteEmail,

    applyPrimaryEmailLocally,
    applyPrimaryPhoneLocally,
  };

  return (
    <ContactsContext.Provider value={value}>
      {children}
    </ContactsContext.Provider>
  );
};

export const useContacts = () => {
  const context = useContext(ContactsContext);
  if (!context) {
    throw new Error("useContacts must be used within ContactsProvider");
  }
  return context;
};
